import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
    PiCheckCircleBold,
    PiDownloadSimpleBold,
    PiEyeBold,
    PiFileXlsBold,
    PiWarningBold,
} from 'react-icons/pi';
import { Button, Input, Skeleton, Text } from '@v2/design-system';
import FileDropzone from '@v2/components/ui/FileDropzone';
import V2Modal from '@v2/components/ui/V2Modal';
import api from '@v2/lib/api';
import { useI18n } from '@v2/providers/I18nProvider';

type BatchUser = { id: number | null; name: string | null; avatar_url: string | null };
type ImportRow = {
    row_number: number;
    sku: string;
    product_name: string;
    quantity: number;
    invoice_number: string;
    distribution_center_code: string;
    cd_location_code: string | null;
    current_stock: number;
    resulting_stock: number;
};
type Batch = {
    id: number;
    status: 'validating' | 'validated' | 'invalid' | 'completed';
    file_name: string;
    reason: string | null;
    total_rows: number;
    valid_rows: number;
    error_rows: number;
    total_units: number;
    new_processes?: string[];
    created_at: string;
    confirmed_at: string | null;
    user: BatchUser;
    rows?: ImportRow[];
    errors?: Array<{ row: number | null; message: string }>;
};

type Props = {
    open: boolean;
    onClose: () => void;
    canImport: boolean;
    canViewLog: boolean;
};

export default function ProductStockImportModal({ open, onClose, canImport, canViewLog }: Props) {
    const { t } = useI18n();
    const queryClient = useQueryClient();
    const [tab, setTab] = useState<'import' | 'history'>(canImport ? 'import' : 'history');
    const [reason, setReason] = useState('');
    const [batch, setBatch] = useState<Batch | null>(null);
    const [historyBatchId, setHistoryBatchId] = useState<number | null>(null);

    const history = useQuery({
        queryKey: ['product-stock-import-history'],
        queryFn: async () => (await api.get<{ data: Batch[] }>('/products/stock-import/history')).data.data,
        enabled: open && canViewLog && tab === 'history',
    });

    const historyDetail = useQuery({
        queryKey: ['product-stock-import-history', historyBatchId],
        queryFn: async () => (await api.get<{ data: Batch }>(`/products/stock-import/history/${historyBatchId}`)).data.data,
        enabled: open && canViewLog && tab === 'history' && historyBatchId !== null,
    });

    const preview = useMutation({
        mutationFn: async (file: File) => {
            const form = new FormData();
            form.append('file', file);
            if (reason.trim()) form.append('reason', reason.trim());
            return (await api.post<{ data: Batch }>('/products/stock-import/preview', form)).data.data;
        },
        onSuccess: (data) => {
            setBatch(data);
            if (data.status === 'validated') toast.success(t('v2.stock_import.messages.validated'));
            else toast.error(t('v2.stock_import.messages.has_errors'));
        },
        onError: (error: any) => toast.error(error?.response?.data?.message ?? t('v2.stock_import.messages.upload_error')),
    });

    const confirm = useMutation({
        mutationFn: async () =>
            (await api.post<{ message: string; data: Batch }>(`/products/stock-import/${batch?.id}/confirm`)).data,
        onSuccess: (data) => {
            setBatch(data.data);
            toast.success(data.message);
            void queryClient.invalidateQueries({ queryKey: ['products'] });
            void queryClient.invalidateQueries({ queryKey: ['products-filter-options'] });
            void queryClient.invalidateQueries({ queryKey: ['product-stock-import-history'] });
        },
        onError: (error: any) => toast.error(error?.response?.data?.message ?? t('v2.stock_import.messages.confirm_error')),
    });

    const download = async (url: string, fallbackName: string) => {
        const response = await api.get(url, { responseType: 'blob' });
        const objectUrl = URL.createObjectURL(response.data);
        const anchor = document.createElement('a');
        anchor.href = objectUrl;
        anchor.download = fallbackName;
        document.body.appendChild(anchor);
        anchor.click();
        anchor.remove();
        URL.revokeObjectURL(objectUrl);
    };

    return (
        <V2Modal
            open={open}
            onClose={onClose}
            size="5xl"
            title={t('v2.stock_import.title')}
            description={t('v2.stock_import.description')}
        >
            <div className="mb-5 flex gap-2 border-b border-gray-200 dark:border-gray-700">
                {canImport && (
                    <button
                        type="button"
                        onClick={() => setTab('import')}
                        className={`border-b-2 px-3 py-2 text-sm font-medium ${tab === 'import' ? 'border-primary text-primary' : 'border-transparent text-gray-500'}`}
                    >
                        {t('v2.stock_import.tabs.import')}
                    </button>
                )}
                {canViewLog && (
                    <button
                        type="button"
                        onClick={() => setTab('history')}
                        className={`border-b-2 px-3 py-2 text-sm font-medium ${tab === 'history' ? 'border-primary text-primary' : 'border-transparent text-gray-500'}`}
                    >
                        {t('v2.stock_import.tabs.history')}
                    </button>
                )}
            </div>

            {tab === 'import' && canImport ? (
                <div className="space-y-5">
                    <div className="space-y-4 rounded-xl border border-gray-200 p-4 dark:border-gray-700">
                        <div className="flex flex-wrap items-end gap-3">
                            <Button
                                variant="outline"
                                onClick={() => void download('/products/stock-import/template', 'modelo-importacao-estoque.xlsx')}
                            >
                                <PiDownloadSimpleBold className="me-1.5 size-4" />
                                {t('v2.stock_import.actions.download_template')}
                            </Button>
                            <div className="min-w-64 flex-1">
                                <Text className="mb-1 text-xs font-medium">{t('v2.stock_import.fields.reason')}</Text>
                                <Input
                                    value={reason}
                                    onChange={(event) => setReason(event.target.value)}
                                    placeholder={t('v2.stock_import.fields.reason_placeholder')}
                                />
                            </div>
                        </div>
                        <FileDropzone
                            accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                            maxSizeMb={20}
                            loading={preview.isPending}
                            disabled={preview.isPending}
                            label={t('v2.stock_import.actions.upload')}
                            hint="Arraste a planilha .xlsx ou clique para selecionar"
                            showSelected={false}
                            onFiles={(files) => {
                                if (files[0]) preview.mutate(files[0]);
                            }}
                            onError={(msg) => toast.error(msg)}
                        />
                    </div>

                    {batch && (
                        <div className="space-y-4">
                            <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
                                <Summary label={t('v2.stock_import.summary.rows')} value={batch.total_rows} />
                                <Summary label={t('v2.stock_import.summary.valid')} value={batch.valid_rows} />
                                <Summary label={t('v2.stock_import.summary.errors')} value={batch.error_rows} danger={batch.error_rows > 0} />
                                <Summary label={t('v2.stock_import.summary.units')} value={batch.total_units} />
                            </div>

                            {(batch.new_processes?.length ?? 0) > 0 && batch.status !== 'completed' && (
                                <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900 dark:border-amber-900/50 dark:bg-amber-950/20 dark:text-amber-200">
                                    <p className="font-semibold">{t('v2.stock_import.new_processes_title')}</p>
                                    <p className="mt-1 opacity-90">{t('v2.stock_import.new_processes_hint')}</p>
                                    <div className="mt-2 flex flex-wrap gap-1.5">
                                        {batch.new_processes?.map((code) => (
                                            <span key={code} className="rounded-full border border-amber-300 bg-white px-2 py-0.5 text-xs font-semibold dark:border-amber-800 dark:bg-zinc-900">
                                                {code}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {(batch.errors?.length ?? 0) > 0 && (
                                <div className="rounded-xl border border-rose-200 bg-rose-50 p-4 dark:border-rose-900/50 dark:bg-rose-950/20">
                                    <div className="mb-2 flex items-center gap-2 font-semibold text-rose-700 dark:text-rose-300">
                                        <PiWarningBold /> {t('v2.stock_import.errors_title')}
                                    </div>
                                    <ul className="space-y-1 text-sm text-rose-700 dark:text-rose-300">
                                        {batch.errors?.map((error, index) => (
                                            <li key={`${error.row}-${index}`}>
                                                {error.row ? `${t('v2.stock_import.row')} ${error.row}: ` : ''}{error.message}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}

                            {(batch.rows?.length ?? 0) > 0 && (
                                <div className="overflow-x-auto rounded-xl border border-gray-200 dark:border-gray-700">
                                    <table className="w-full text-left text-sm">
                                        <thead className="bg-gray-50 text-xs text-gray-500 dark:bg-gray-800">
                                            <tr>
                                                <th className="px-3 py-2">{t('v2.stock_import.columns.sku')}</th>
                                                <th className="px-3 py-2">{t('v2.stock_import.columns.invoice')}</th>
                                                <th className="px-3 py-2">{t('v2.stock_import.columns.location')}</th>
                                                <th className="px-3 py-2 text-right">{t('v2.stock_import.columns.quantity')}</th>
                                                <th className="px-3 py-2 text-right">{t('v2.stock_import.columns.stock_change')}</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {batch.rows?.map((row) => (
                                                <tr key={row.row_number} className="border-t border-gray-100 dark:border-gray-800">
                                                    <td className="px-3 py-2 font-medium">{row.sku}<div className="text-xs font-normal text-gray-500">{row.product_name}</div></td>
                                                    <td className="px-3 py-2">{row.invoice_number}</td>
                                                    <td className="px-3 py-2">{row.distribution_center_code}{row.cd_location_code ? ` · ${row.cd_location_code}` : ''}</td>
                                                    <td className="px-3 py-2 text-right">{row.quantity}</td>
                                                    <td className="px-3 py-2 text-right">{row.current_stock} → {row.resulting_stock}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}

                            {batch.status === 'validated' && (
                                <div className="flex justify-end">
                                    <Button onClick={() => confirm.mutate()} isLoading={confirm.isPending}>
                                        <PiCheckCircleBold className="me-1.5 size-4" />
                                        {t('v2.stock_import.actions.confirm')}
                                    </Button>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            ) : (
                <div className="space-y-3">
                    {historyBatchId !== null ? (
                        <div className="space-y-4">
                            <Button variant="outline" size="sm" onClick={() => setHistoryBatchId(null)}>
                                {t('v2.stock_import.actions.back')}
                            </Button>
                            {historyDetail.isLoading ? (
                                <Skeleton className="h-52 w-full rounded-xl" />
                            ) : historyDetail.data ? (
                                <>
                                    <div className="flex items-center gap-3 rounded-xl border border-gray-200 p-4 dark:border-gray-700">
                                        <UserAvatar user={historyDetail.data.user} />
                                        <div className="flex-1">
                                            <p className="font-semibold text-gray-900 dark:text-gray-100">{historyDetail.data.file_name}</p>
                                            <p className="text-xs text-gray-500">{historyDetail.data.user.name ?? '—'} · {new Date(historyDetail.data.created_at).toLocaleString()}</p>
                                        </div>
                                        <p className="text-sm font-semibold">{historyDetail.data.total_units} {t('v2.stock_import.summary.units').toLowerCase()}</p>
                                    </div>
                                    {(historyDetail.data.rows?.length ?? 0) > 0 && (
                                        <div className="overflow-x-auto rounded-xl border border-gray-200 dark:border-gray-700">
                                            <table className="w-full text-left text-sm">
                                                <thead className="bg-gray-50 text-xs text-gray-500 dark:bg-gray-800">
                                                    <tr>
                                                        <th className="px-3 py-2">{t('v2.stock_import.row')}</th>
                                                        <th className="px-3 py-2">{t('v2.stock_import.columns.sku')}</th>
                                                        <th className="px-3 py-2">{t('v2.stock_import.columns.invoice')}</th>
                                                        <th className="px-3 py-2">{t('v2.stock_import.columns.location')}</th>
                                                        <th className="px-3 py-2 text-right">{t('v2.stock_import.columns.quantity')}</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {historyDetail.data.rows?.map((row) => (
                                                        <tr key={row.row_number} className="border-t border-gray-100 dark:border-gray-800">
                                                            <td className="px-3 py-2">{row.row_number}</td>
                                                            <td className="px-3 py-2 font-medium">{row.sku}<div className="text-xs font-normal text-gray-500">{row.product_name}</div></td>
                                                            <td className="px-3 py-2">{row.invoice_number}</td>
                                                            <td className="px-3 py-2">{row.distribution_center_code}{row.cd_location_code ? ` · ${row.cd_location_code}` : ''}</td>
                                                            <td className="px-3 py-2 text-right">{row.quantity}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}
                                    {(historyDetail.data.errors?.length ?? 0) > 0 && (
                                        <ul className="rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700 dark:border-rose-900/50 dark:bg-rose-950/20 dark:text-rose-300">
                                            {historyDetail.data.errors?.map((error, index) => (
                                                <li key={`${error.row}-${index}`}>{error.row ? `${t('v2.stock_import.row')} ${error.row}: ` : ''}{error.message}</li>
                                            ))}
                                        </ul>
                                    )}
                                </>
                            ) : null}
                        </div>
                    ) : history.isLoading ? (
                        Array.from({ length: 4 }, (_, index) => <Skeleton key={index} className="h-20 w-full rounded-xl" />)
                    ) : history.data?.length ? (
                        history.data.map((item) => (
                            <div key={item.id} className="flex flex-wrap items-center gap-4 rounded-xl border border-gray-200 p-4 dark:border-gray-700">
                                <UserAvatar user={item.user} />
                                <div className="min-w-48 flex-1">
                                    <p className="font-medium text-gray-900 dark:text-gray-100">{item.file_name}</p>
                                    <p className="text-xs text-gray-500">
                                        {item.user.name ?? '—'} · {new Date(item.created_at).toLocaleString()}
                                    </p>
                                    {item.reason && <p className="mt-1 text-xs text-gray-500">{item.reason}</p>}
                                </div>
                                <div className="text-right text-sm">
                                    <p className="font-semibold">{item.total_units} {t('v2.stock_import.summary.units').toLowerCase()}</p>
                                    <p className={item.status === 'completed' ? 'text-emerald-600' : item.status === 'invalid' ? 'text-rose-600' : 'text-amber-600'}>
                                        {t(`v2.stock_import.status.${item.status}`)}
                                    </p>
                                </div>
                                <Button size="sm" variant="outline" onClick={() => setHistoryBatchId(item.id)}>
                                    <PiEyeBold className="me-1.5 size-4" />
                                    {t('v2.stock_import.actions.details')}
                                </Button>
                                <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => void download(`/products/stock-import/history/${item.id}/download`, item.file_name)}
                                >
                                    <PiFileXlsBold className="me-1.5 size-4" />
                                    {t('v2.stock_import.actions.file')}
                                </Button>
                            </div>
                        ))
                    ) : (
                        <div className="py-12 text-center text-gray-500">{t('v2.stock_import.empty_history')}</div>
                    )}
                </div>
            )}
        </V2Modal>
    );
}

function Summary({ label, value, danger = false }: { label: string; value: number; danger?: boolean }) {
    return (
        <div className="rounded-xl border border-gray-200 p-3 dark:border-gray-700">
            <p className={`text-2xl font-bold ${danger ? 'text-rose-600' : 'text-gray-900 dark:text-gray-100'}`}>{value}</p>
            <p className="text-xs text-gray-500">{label}</p>
        </div>
    );
}

function UserAvatar({ user }: { user: BatchUser }) {
    if (user.avatar_url) {
        return <img src={user.avatar_url} alt={user.name ?? ''} className="size-10 rounded-full object-cover" />;
    }

    const initials = (user.name ?? '?').split(/\s+/).slice(0, 2).map((part) => part[0]).join('').toUpperCase();
    return <div className="flex size-10 items-center justify-center rounded-full bg-sky-100 text-sm font-bold text-sky-700">{initials}</div>;
}
